import {
  type Flight,
  ShopFilter,
  type Suggestion,
} from "@b2bportal/air-shopping-api";
import {
  type AirlineCode,
  type FareclassOptionFilter,
  type FlightPricePrediction,
  type IFlightNumberFilter,
  type IPassengerCounts,
  type InputErrorType,
  type ITimeRange,
  type ITripTerminus,
  type MobileFlightSearchDialogSteps,
  type MulticityRouteValues,
  type SerializableMonthBucket,
  SliceStopCountFilter,
} from "@b2bportal/core-types";
import { TripCategory } from "@overrides/types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { fetchCalendar, setSearchFormFromItinerary } from "./thunks";
import { initializeSearchState } from "./thunks/fetchSearchState";

export type FlightLocationSuggestion = Suggestion & {
  id: { Id: "Flight" } & Flight;
};

export type FormErrors = {
  origin?: InputErrorType;
  destination?: InputErrorType;
  dates?: InputErrorType;
  multicityRoutes?: {
    origin?: InputErrorType;
    destination?: InputErrorType;
    dates?: InputErrorType;
  }[];
};

export interface IFilterState {
  stopsOption: SliceStopCountFilter;
  maxPriceFilter: number;
  fareclassOptionFilter: FareclassOptionFilter;
  outboundDepartureTimeRange: ITimeRange;
  outboundArrivalTimeRange: ITimeRange;
  returnDepartureTimeRange: ITimeRange;
  returnArrivalTimeRange: ITimeRange;
  airlineFilter: AirlineCode[];
  airportFilter: string[];
  flightNumberFilter: IFlightNumberFilter[];
}

export interface FlightSearchValues {
  tripCategory: TripCategory;
  origin?: ITripTerminus;
  destination?: ITripTerminus;
  departureDate?: string;
  returnDate?: string;
  passengerCounts: {
    adultsCount: number;
    childrenCount: number;
    infantsInSeatCount: number;
    infantsOnLapCount: number;
  };
  tripFilter: ShopFilter;
  multicityRoutes: MulticityRouteValues[];
}

export type StoreFlightPricePrediction = FlightPricePrediction & {
  departureMonths: SerializableMonthBucket[];
};

export interface FlightSearchState extends FlightSearchValues, IFilterState {
  isInitializingValues?: boolean;
  searchFormValues: FlightSearchValues;
  mobileSearchActiveStep: MobileFlightSearchDialogSteps | null;
  pricePrediction: StoreFlightPricePrediction;
  // Array of price predictions for multicity flights, indexed by slice number
  multicityPricePredictions: StoreFlightPricePrediction[];
  formError: FormErrors;
}

export const INITIAL_MAX_PRICE = 0;

export const MAXIMUM_COUNT = 6;
export const MINIMUM_ADULT_COUNT = 1;
export const TIME_RANGE_STEP = 360;
export const TIME_RANGE_MAX = 1440;

const initialTimeRange: ITimeRange = {
  min: 0,
  // note: time measured in minutes
  max: TIME_RANGE_MAX,
};

export const initialFareclassOptionFilter: FareclassOptionFilter = {
  basic: false,
  standard: false,
  enhanced: false,
  premium: false,
  luxury: false,
};

export const initialFilterOptions: IFilterState = {
  stopsOption: SliceStopCountFilter.ANY_NUMBER,
  maxPriceFilter: Number.MAX_SAFE_INTEGER,
  fareclassOptionFilter: initialFareclassOptionFilter,
  outboundDepartureTimeRange: initialTimeRange,
  outboundArrivalTimeRange: initialTimeRange,
  returnDepartureTimeRange: initialTimeRange,
  returnArrivalTimeRange: initialTimeRange,
  airlineFilter: [],
  airportFilter: [],
  flightNumberFilter: [],
};

export const initialPricePrediction: StoreFlightPricePrediction = {
  departureMonths: [],
  priceTags: [],
  isLoading: false,
};

export const initialSearchState: FlightSearchState = {
  tripCategory: TripCategory.ROUND_TRIP,
  tripFilter: ShopFilter.NoFilter,
  passengerCounts: {
    adultsCount: 1,
    childrenCount: 0,
    infantsInSeatCount: 0,
    infantsOnLapCount: 0,
  },
  multicityRoutes: [],
  // Need a way to differenciate between "has not started initializeing" and "has finished initializing"
  isInitializingValues: undefined,
  searchFormValues: {
    tripCategory: TripCategory.ROUND_TRIP,
    passengerCounts: {
      adultsCount: 1,
      childrenCount: 0,
      infantsInSeatCount: 0,
      infantsOnLapCount: 0,
    },
    tripFilter: ShopFilter.NoFilter,
    multicityRoutes: [
      {
        origin: undefined,
        destination: undefined,
        departureDate: undefined,
      },
      {
        origin: undefined,
        destination: undefined,
        departureDate: undefined,
      },
    ],
  },
  mobileSearchActiveStep: null,
  pricePrediction: initialPricePrediction,
  multicityPricePredictions: [],
  formError: {},
  ...initialFilterOptions,
};

export const flightSearchSlice = createSlice({
  name: "flightSearch",
  initialState: initialSearchState,
  reducers: {
    setTripCategory: (state, action: PayloadAction<TripCategory>) => {
      state.tripCategory = action.payload;
    },
    setOrigin: (state, action: PayloadAction<ITripTerminus>) => {
      state.origin = action.payload;
    },
    setDestination: (state, action: PayloadAction<ITripTerminus>) => {
      state.destination = action.payload;
    },
    setDepartureDate: (state, action: PayloadAction<string>) => {
      state.departureDate = action.payload;
    },
    setReturnDate: (state, action: PayloadAction<string>) => {
      state.returnDate = action.payload;
    },
    setPassengerCount: (state, action: PayloadAction<IPassengerCounts>) => {
      Object.assign(state, action.payload);
    },
    setFlightSearch: (
      state,
      action: PayloadAction<Partial<FlightSearchValues>>
    ) => {
      Object.assign(state, action.payload);
    },
    setStopsOption(state, action: PayloadAction<SliceStopCountFilter>) {
      state.stopsOption = action.payload;
    },
    setTripFilter(state, action: PayloadAction<ShopFilter>) {
      state.tripFilter = action.payload;
    },
    setMaxPriceFilter(state, action: PayloadAction<number>) {
      state.maxPriceFilter = action.payload;
    },
    setFareclassOptionFilter(
      state,
      action: PayloadAction<FareclassOptionFilter>
    ) {
      state.fareclassOptionFilter = action.payload;
    },
    setOutboundDepartureTimeRange(state, action: PayloadAction<ITimeRange>) {
      state.outboundDepartureTimeRange = action.payload;
    },
    setOutboundArrivalTimeRange(state, action: PayloadAction<ITimeRange>) {
      state.outboundArrivalTimeRange = action.payload;
    },
    setReturnDepartureTimeRange(state, action: PayloadAction<ITimeRange>) {
      state.returnDepartureTimeRange = action.payload;
    },
    setReturnArrivalTimeRange(state, action: PayloadAction<ITimeRange>) {
      state.returnArrivalTimeRange = action.payload;
    },
    setAirlineFilter(state, action: PayloadAction<AirlineCode[]>) {
      state.airlineFilter = action.payload;
    },
    setAirportFilter(state, action: PayloadAction<string[]>) {
      state.airportFilter = action.payload;
    },
    setFlightNumberFilter(state, action: PayloadAction<IFlightNumberFilter[]>) {
      state.flightNumberFilter = action.payload;
    },
    resetFilters(state) {
      Object.assign(state, initialFilterOptions);
    },
    setAllFilters(state, action: PayloadAction<IFilterState>) {
      Object.assign(state, action.payload);
    },
    setSearchFormValues(state, action: PayloadAction<FlightSearchValues>) {
      state.searchFormValues = action.payload;
    },
    resetSearchForRefetch(state) {
      // Clear location and date fields to force isSameSearch to return false
      // This is used when there's an error and the user manually triggers a new search
      state.origin = undefined;
      state.destination = undefined;
      state.departureDate = undefined;
      state.returnDate = undefined;
      state.multicityRoutes = [];
    },
    setSearchFormTripCategory: (state, action: PayloadAction<TripCategory>) => {
      state.searchFormValues.tripCategory = action.payload;
      // Clear form errors when switching trip types
      state.formError = {};
      // Clear appropriate price predictions when switching trip types
      if (
        action.payload === TripCategory.ONE_WAY ||
        action.payload === TripCategory.ROUND_TRIP
      ) {
        state.multicityPricePredictions = [];
      } else {
        state.pricePrediction = initialPricePrediction;
      }
    },
    setSearchFormOrigin: (state, action: PayloadAction<ITripTerminus>) => {
      state.searchFormValues.origin = action.payload;
    },
    setSearchFormDestination: (state, action: PayloadAction<ITripTerminus>) => {
      state.searchFormValues.destination = action.payload;
    },
    setSearchFormDepartureDate: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.searchFormValues.departureDate = action.payload;
    },
    setSearchFormReturnDate: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.searchFormValues.returnDate = action.payload;
    },
    setSearchFormPassengerCount: (
      state,
      action: PayloadAction<IPassengerCounts>
    ) => {
      Object.assign(state.searchFormValues.passengerCounts, action.payload);
    },
    setSearchFormTripFilter: (state, action: PayloadAction<ShopFilter>) => {
      state.searchFormValues.tripFilter = action.payload;
    },
    setSearchFormMulticityOrigin: (
      state,
      action: PayloadAction<{ origin: ITripTerminus; index: number }>
    ) => {
      const { index, origin } = action.payload;
      const updatedRoute = {
        ...state.searchFormValues.multicityRoutes[index],
        origin: origin,
      };

      state.searchFormValues.multicityRoutes[index] = updatedRoute;
    },
    setSearchFormMulticityDestination: (
      state,
      action: PayloadAction<{ destination: ITripTerminus; index: number }>
    ) => {
      const { index, destination } = action.payload;
      const updatedRoute = {
        ...state.searchFormValues.multicityRoutes[index],
        destination: destination,
      };

      if (
        index === 0 &&
        state.searchFormValues.multicityRoutes[1].origin == null
      ) {
        state.searchFormValues.multicityRoutes[1].origin = destination;
      }

      state.searchFormValues.multicityRoutes[index] = updatedRoute;
    },
    setSearchFormMulticityDepartureDate: (
      state,
      action: PayloadAction<{ departureDate: string; index: number }>
    ) => {
      const { index, departureDate } = action.payload;
      const updatedRoute = {
        ...state.searchFormValues.multicityRoutes[index],
        departureDate,
      };

      state.searchFormValues.multicityRoutes[index] = updatedRoute;
    },
    setSearchFormMulticityReturnDate: (
      state,
      action: PayloadAction<{ returnDate: string; index: number }>
    ) => {
      const { index, returnDate } = action.payload;
      const updatedRoute = {
        ...state.searchFormValues.multicityRoutes[index],
        returnDate,
      };

      state.searchFormValues.multicityRoutes[index] = updatedRoute;
    },
    addMulticityRoute: (state) => {
      const lastSelectedDestination =
        state.searchFormValues.multicityRoutes[
          state.searchFormValues.multicityRoutes.length - 1
        ].destination;
      const newRoute = {
        origin: lastSelectedDestination,
        destination: undefined,
        departureDate: undefined,
      };

      state.searchFormValues.multicityRoutes.push(newRoute);
    },
    removeMulticityRoute: (state, action: PayloadAction<number>) => {
      state.searchFormValues.multicityRoutes.splice(action.payload, 1);
      // Also remove the corresponding price prediction
      if (state.multicityPricePredictions.length > action.payload) {
        state.multicityPricePredictions.splice(action.payload, 1);
      }
      // Also remove the corresponding form error
      if (
        state.formError.multicityRoutes &&
        state.formError.multicityRoutes.length > action.payload
      ) {
        state.formError.multicityRoutes.splice(action.payload, 1);
      }
    },
    setFormError: (state, action: PayloadAction<FormErrors>) => {
      state.formError = action.payload;
    },
    clearOriginError: (state) => {
      state.formError.origin = undefined;
    },
    clearDestinationError: (state) => {
      state.formError.destination = undefined;
    },
    clearDatesError: (state) => {
      state.formError.dates = undefined;
    },
    clearMulticityOriginError: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (!state.formError.multicityRoutes) {
        state.formError.multicityRoutes = [];
      }
      if (!state.formError.multicityRoutes[index]) {
        state.formError.multicityRoutes[index] = {};
      }
      state.formError.multicityRoutes[index].origin = undefined;
    },
    clearMulticityDestinationError: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (!state.formError.multicityRoutes) {
        state.formError.multicityRoutes = [];
      }
      if (!state.formError.multicityRoutes[index]) {
        state.formError.multicityRoutes[index] = {};
      }
      state.formError.multicityRoutes[index].destination = undefined;
    },
    clearMulticityDatesError: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (!state.formError.multicityRoutes) {
        state.formError.multicityRoutes = [];
      }
      if (!state.formError.multicityRoutes[index]) {
        state.formError.multicityRoutes[index] = {};
      }
      state.formError.multicityRoutes[index].dates = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchFlights
      .addCase(initializeSearchState.pending, (state) => {
        state.isInitializingValues = true;
      })
      .addCase(initializeSearchState.fulfilled, (state, action) => {
        state.isInitializingValues = false;
        Object.assign(state, initialFilterOptions);
        Object.assign(state, action.payload);
        Object.assign(state.searchFormValues, action.payload);
      })
      .addCase(initializeSearchState.rejected, (state) => {
        state.isInitializingValues = false;
      })
      .addCase(fetchCalendar.pending, (state, action) => {
        // For multicity flights, sliceIndex indicates which leg's calendar is loading
        const sliceIndex = action.meta.arg.sliceIndex;

        if (sliceIndex !== undefined) {
          // Auto-grow array if needed for multicity slices
          while (state.multicityPricePredictions.length <= sliceIndex) {
            state.multicityPricePredictions.push({
              ...initialPricePrediction,
            });
          }
          state.multicityPricePredictions[sliceIndex].priceTags = [];
          state.multicityPricePredictions[sliceIndex].departureMonths = [];
          state.multicityPricePredictions[sliceIndex].isLoading = true;
        } else {
          // For round-trip/one-way, use single prediction
          state.pricePrediction.priceTags = [];
          state.pricePrediction.departureMonths = [];
          state.pricePrediction.isLoading = true;
        }
      })
      .addCase(fetchCalendar.fulfilled, (state, action) => {
        // For multicity flights, sliceIndex indicates which slice's calendar data to store
        const { sliceIndex, ...data } = action.payload;

        if (sliceIndex !== undefined) {
          // Auto-grow array if needed for multicity slices
          while (state.multicityPricePredictions.length <= sliceIndex) {
            state.multicityPricePredictions.push({
              ...initialPricePrediction,
            });
          }
          Object.assign(state.multicityPricePredictions[sliceIndex], data);
          state.multicityPricePredictions[sliceIndex].isLoading = false;
        } else {
          // For round-trip/one-way, use single prediction
          Object.assign(state.pricePrediction, data);
          state.pricePrediction.isLoading = false;
        }
      })
      .addCase(fetchCalendar.rejected, (state, action) => {
        // For multicity flights, sliceIndex indicates which leg's calendar failed to load
        const sliceIndex = action.meta.arg.sliceIndex;

        if (sliceIndex !== undefined) {
          // Auto-grow array if needed for multicity slices
          while (state.multicityPricePredictions.length <= sliceIndex) {
            state.multicityPricePredictions.push({
              ...initialPricePrediction,
            });
          }
          state.multicityPricePredictions[sliceIndex] = {
            ...initialPricePrediction,
          };
        } else {
          // For round-trip/one-way, reset single prediction
          state.pricePrediction = initialPricePrediction;
        }
      })
      .addCase(setSearchFormFromItinerary, (state, action) => {
        Object.assign(state.searchFormValues, action.payload.searchFormValues);
      });
  },
});

export const {
  setTripCategory,
  setOrigin,
  setDestination,
  setDepartureDate,
  setReturnDate,
  setPassengerCount,
  setStopsOption,
  setTripFilter,
  setMaxPriceFilter,
  setFareclassOptionFilter,
  setOutboundDepartureTimeRange,
  setOutboundArrivalTimeRange,
  setReturnDepartureTimeRange,
  setReturnArrivalTimeRange,
  setAirlineFilter,
  setAirportFilter,
  setFlightNumberFilter,
  setSearchFormValues,
  resetSearchForRefetch,
  setSearchFormOrigin,
  setSearchFormDestination,
  setSearchFormTripCategory,
  setSearchFormPassengerCount,
  setSearchFormDepartureDate,
  setSearchFormReturnDate,
  setSearchFormTripFilter,
  setSearchFormMulticityOrigin,
  setSearchFormMulticityDestination,
  setSearchFormMulticityDepartureDate,
  setSearchFormMulticityReturnDate,
} = flightSearchSlice.actions;

export const FlightSearchActions = flightSearchSlice.actions;
export default flightSearchSlice.reducer;
